import React, { Fragment, useEffect, useState } from "react";
import PatientNotesItem from "./PatientNotesItem";
import { createNote, formatDate, getAllNotes } from "./helpers";
import { connect } from "react-redux";
import { setCurrentTreatmentPlan } from "../../CreateTPlan_1Upload/actions";
import PropTypes from "prop-types";
import { EMPTY_STRING } from "../../../constants";
import {
    PATIENT_SHARE_ADD_NOTE_PLACEHOLDER,
    PATIENT_SHARE_NO_NOTES,
    SHARE_AND_NOTES_HEADERS,
} from "./constants";
import CommentIcon from "../../../ui/icons/CommentIcon";
import Button from "../../../ui/Button";
import ArrowIcon from "../../../ui/icons/ArrowIcon";
import SharePatientCard from "./SharePatientCard";
import Alert from "../Alert";
import { sanitizeUserText } from "../../../helpers/helpers";

function ShareAndNotesTab(props) {
    const [loading, setLoading] = useState(true);
    const [notes, setNotes] = useState([]);
    const [newNoteContent, setNewNoteContent] = useState(EMPTY_STRING);
    const [noteError, setNoteError] = useState(EMPTY_STRING);

    // TODO: Cleanup component, refactor useEffect(), look into using useCallback()
    useEffect(() => {
        if (
            props.currentTreatmentPlan &&
            props.currentTreatmentPlan.TreatmentPlan &&
            props.currentTreatmentPlan.TreatmentPlan.PatientData
        ) {
            updatePatientNotes();
        }
    }, []);

    async function updatePatientNotes() {
        const getNotesRequest = {
            token: props.authToken,
            patientUuid:
                props.currentTreatmentPlan.TreatmentPlan.PatientData
                    .PatientUUID,
            planUuid:
                props.currentTreatmentPlan.TreatmentPlan.HeaderData.PlanUUID,
        };
        const getNotesResponse = await getAllNotes(getNotesRequest);

        if (getNotesResponse.payload["PlanNotes"]) {
            const planNotes = getNotesResponse.payload["PlanNotes"];
            await handleUpdatePlanNotesInTreatmentPlan(planNotes);

            const allPlanNotes = [];
            Object.keys(planNotes).forEach((stepKey) => {
                const stepNotes = planNotes[stepKey];
                allPlanNotes.push(...stepNotes);
            });

            allPlanNotes.sort(
                (note1, note2) =>
                    new Date(note1["Timestamp"]) - new Date(note2["Timestamp"])
            );
            setNotes(allPlanNotes);
        }

        setLoading(false);
    }

    async function handleUpdatePlanNotesInTreatmentPlan(planNotes) {
        const { currentTreatmentPlan, setCurrentTreatmentPlan } = props;

        const updatedPlan = {
            ...currentTreatmentPlan,
            TreatmentPlan: {
                ...currentTreatmentPlan.TreatmentPlan,
                AdditionalData: {
                    ...currentTreatmentPlan.TreatmentPlan.AdditionalData,
                    PlanNotes: planNotes,
                },
            },
        };
        setCurrentTreatmentPlan(updatedPlan);
    }

    function buildPatientNotesList() {
        if (loading) {
            return;
        }

        if (notes.length === 0) {
            return (
                <div className="px-6 py-4 text-neutral-400">
                    {PATIENT_SHARE_NO_NOTES}
                </div>
            );
        }

        return notes.map((note, index) => {
            const formattedDate = formatDate(note["Timestamp"]);
            return (
                <Fragment key={index}>
                    <PatientNotesItem
                        doctorName={note["Username"]}
                        doctorEmail={note["Email"]}
                        date={formattedDate}
                        note={note["Content"]}
                    />
                </Fragment>
            );
        });
    }

    function handleTextAreaChange(e) {
        setNewNoteContent(e.target.value);
        if (noteError) setNoteError(EMPTY_STRING);
    }

    function clearNoteError() {
        setTimeout(() => {
            setNoteError(EMPTY_STRING);
        }, 3000);
    }

    async function handleCreateNote(step) {
        const sanitizedContent = sanitizeUserText(newNoteContent);

        if (!sanitizedContent || sanitizedContent.trim() === EMPTY_STRING) {
            setNoteError(
                "Your note contains invalid content. Please remove any special formatting and try again."
            );
            clearNoteError();
            return;
        }

        const createNoteRequest = {
            token: props.authToken,
            userUuid: props.userData.uuid,
            patientUuid:
                props.currentTreatmentPlan.TreatmentPlan.PatientData
                    .PatientUUID,
            planUuid:
                props.currentTreatmentPlan.TreatmentPlan.HeaderData.PlanUUID,
            step: step,
            content: sanitizedContent,
        };

        const response = await createNote(createNoteRequest);
        let noteError = "";

        if (response?.payload.status === "INVALID_INPUT") {
            noteError =
                "Unable to post note. Please remove any special characters or formatting and try again.";
        } else if (!response || response.payload.status !== "SUCCESS") {
            noteError = "Failed to post note. Please try again.";
        }

        if (noteError) {
            setNoteError(noteError);
            clearNoteError();
            return;
        }

        setNoteError(EMPTY_STRING);
        setNewNoteContent(EMPTY_STRING);
        await updatePatientNotes();
    }

    return (
        <div className="p-4">
            <SharePatientCard
                authToken={props.authToken}
                doctorList={props.doctorList}
                userUUID={props.userData.uuid}
                patientUUID={
                    props.currentTreatmentPlan.TreatmentPlan.PatientData
                        .PatientUUID
                }
                planUUID={
                    props.currentTreatmentPlan.TreatmentPlan.HeaderData.PlanUUID
                }
            />
            <div className="mt-4 w-full rounded-lg shadow-[0px_0px_5px_0px_rgba(0,0,0,0.1)]">
                <div className="w-full border-b border-b-neutral-100 px-6 pt-3 pb-2">
                    <div className="flex items-center gap-2">
                        <CommentIcon />
                        <div className="text-xl">
                            {SHARE_AND_NOTES_HEADERS.PATIENT_NOTES}
                        </div>
                    </div>
                </div>
                <div id="patient-notes-ctnr" className="h-64 overflow-y-auto">
                    {buildPatientNotesList()}
                </div>
                <div className="flex items-center justify-between gap-2 border-t border-t-neutral-100 p-4">
                    <div className="flex-1">
                        <textarea
                            id="add-note-textarea"
                            className={`tw-rounded-sm h-14 w-full resize-none overflow-y-auto border-1 px-2 py-1 align-middle focus:outline-hidden ${
                                noteError
                                    ? "border-(--errorRed)"
                                    : "border-neutral-100"
                            }`}
                            placeholder={PATIENT_SHARE_ADD_NOTE_PLACEHOLDER}
                            value={newNoteContent}
                            onChange={handleTextAreaChange}
                            aria-describedby={
                                noteError ? "note-error-msg" : undefined
                            }
                            aria-invalid={!!noteError}
                        />
                    </div>
                    <Button
                        id="add-note"
                        className="shrink-0"
                        onClick={() => handleCreateNote(props.step.toString())}
                        disabled={newNoteContent === EMPTY_STRING}
                        variant="primary"
                        customHeight="h-14"
                        customWidth="w-12"
                    >
                        <ArrowIcon className="shrink-0" />
                    </Button>
                </div>
                {noteError && <Alert variant="danger" text={noteError} />}
            </div>
        </div>
    );
}

ShareAndNotesTab.propTypes = {
    authToken: PropTypes.string.isRequired,
    userData: PropTypes.object.isRequired,
    currentTreatmentPlan: PropTypes.object,
    setCurrentTreatmentPlan: PropTypes.func,
    step: PropTypes.number.isRequired,
    doctorList: PropTypes.array,
};

const mapStateToProps = function (state) {
    return {
        userData: state.LoginReducer.userData,
    };
};

const mapDispatchToProps = function (dispatch) {
    return {
        setCurrentTreatmentPlan: (plan) =>
            dispatch(setCurrentTreatmentPlan(plan)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(ShareAndNotesTab);
